
import React from 'react';
import { RotateCcw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompressionResult } from '@/types/imageFile';
import { downloadFile } from '@/utils/imageCompression';
import { useAnalytics } from '@/hooks/useAnalytics';

interface ActionButtonsProps {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  retryCount: number;
  maxRetryAttempts: number;
  compression?: CompressionResult;
  fileName: string;
  onCompress: () => void;
  onRetry: () => void;
  isPremium?: boolean;
}

const ActionButtons = ({ 
  status, 
  retryCount, 
  maxRetryAttempts, 
  compression,
  fileName, 
  onRetry,
  isPremium = false 
}: ActionButtonsProps) => {
  const { trackEvent } = useAnalytics();

  const handleDownloadCompressed = () => {
    if (compression) {
      const compressedKb = Math.round(compression.compressedSize / 1024);
      const reductionPercent = Math.round(compression.compressionRatio);
      const fileExt = compression.compressedFile.type || fileName.split('.').pop() || 'unknown';
      trackEvent('download_click', {
        file_type: fileExt,
        compressed_size_kb: compressedKb,
        reduction_percent: reductionPercent,
      });
      downloadFile(compression.compressedFile, `compressed_${fileName}`);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {status === 'completed' && compression && (
        <Button
          onClick={handleDownloadCompressed}
          size="lg"
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
        >
          <Download className="w-5 h-5 mr-2" />
          Descargar imagen optimizada
        </Button>
      )}

      {status === 'failed' && retryCount < maxRetryAttempts && (
        <Button
          onClick={onRetry}
          size="sm"
          variant="outline"
          className="w-full"
        >
          <RotateCcw className="w-4 h-4 mr-1" />
          Reintentar
        </Button>
      )}

      {status === 'failed' && retryCount >= maxRetryAttempts && (
        <div className="text-center text-xs text-destructive py-2">
          Máximo de intentos alcanzado
        </div>
      )}
    </div>
  );
};

export default ActionButtons;
